import React, { FC } from 'react';
import * as Styled from './Form.styles';

import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/function';
import { HttpError, HttpStatusCode } from '@core/http';
import { AuthenticateParams, authenticateSchema } from '@modules/auth/model';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@shared/modules/translation/hooks';
import { Input, PasswordInput } from '@styles/shared/form/Input';
import FormField from '@shared/modules/form/components/FormField';
import { HttpErrorMessage } from '@shared/components/message/Message';
import { Translations } from '@shared/modules/translation/locales';
import CustomLink from '@shared/components/link/CustomLink';

function getLabelBuilder(error: O.Option<HttpError>, translations: Translations) {
  return pipe(
    error,
    O.fold(
      () => null,
      err => {
        if (err.status === HttpStatusCode.UNAUTHORIZED) return translations.error.message.unauthorized;
        if (err.status === HttpStatusCode.FORBIDDEN) return translations.signIn.forbidden;
        return null;
      },
    ),
  );
}

function getLinkBuilder(error: O.Option<HttpError>, translations: Translations) {
  return pipe(
    error,
    O.fold(
      () => null,
      err => {
        if (err.status === HttpStatusCode.FORBIDDEN)
          return <CustomLink to="contact">{translations.signIn.forbiddenContact}</CustomLink>;
        return null;
      },
    ),
  );
}

interface SignInFormProps {
  loading: boolean;
  error: O.Option<HttpError>;
  onSubmit: (params: AuthenticateParams) => void;
}

const SignInForm: FC<SignInFormProps> = ({ loading, error, onSubmit }) => {
  const translations = useTranslations();

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<AuthenticateParams>({
    resolver: zodResolver(authenticateSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const labelBuilder = getLabelBuilder(error, translations);
  const linkBuilder = getLinkBuilder(error, translations);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Styled.FormContainer>
        <h1>{translations.signIn.title}</h1>

        <Styled.FormContent>
          <HttpErrorMessage error={error} labelBuilder={labelBuilder}>
            {linkBuilder}
          </HttpErrorMessage>

          <Styled.FormInput label={translations.signIn.email} htmlFor="email" required>
            <Input
              id="email"
              type="email"
              placeholder={translations.signIn.emailPlaceholder}
              error={!!errors.email}
              {...register('email')}
            />
          </Styled.FormInput>

          <FormField label={translations.signIn.password} htmlFor="password" required>
            <PasswordInput
              id="password"
              placeholder={translations.signIn.password}
              error={!!errors.password}
              {...register('password')}
            />
          </FormField>

          <Styled.FormButton type="submit" $loading={loading}>
            {translations.signIn.login}
          </Styled.FormButton>

          <Styled.FormLink to="forgot-password">{translations.signIn.forgotPassword}</Styled.FormLink>
        </Styled.FormContent>

        <Styled.FormDivider />

        <Styled.FormWithoutAccount>{translations.signIn.newAccount}</Styled.FormWithoutAccount>

        <Styled.FormLink to="sign-up" queries={{ referrer: 'sign-in' }}>
          {translations.signIn.newAccountLink}
        </Styled.FormLink>
      </Styled.FormContainer>
    </form>
  );
};

export default SignInForm;
